import React, { useState } from 'react';
import { Plus, Trash2, Upload, Image as ImageIcon, Link as LinkIcon, X } from 'lucide-react';
import { uploadApi } from '../../api';

/**
 * 规格编辑组件
 * specs: [{ name, name_en, name_km, values: [{ value, value_en, value_km }] }]
 * skus:  [{ price, vip_price, stock, image, sku_code, spec_values: [{ spec_index, value_index }] }]
 */
const ProductSpecEditor = ({ specs, skus, onChange, mainPrice, mainVipPrice }) => {

  const [uploadingSkuIndex, setUploadingSkuIndex] = useState(null);
  const [urlInputIndex, setUrlInputIndex] = useState(null);
  const [urlInputValue, setUrlInputValue] = useState('');
  const [urlErrorIndex, setUrlErrorIndex] = useState(null);

  // 图片放大预览
  const [zoomImage, setZoomImage] = useState(null); // { src, label } | null

  // 生成所有SKU组合
  const generateSkuCombinations = (newSpecs) => {
    if (!newSpecs || newSpecs.length === 0) return [];

    const validSpecs = newSpecs.filter(s => s.values && s.values.length > 0);
    if (validSpecs.length === 0) return [];

    // 笛卡尔积
    let combinations = [[]];
    for (let si = 0; si < newSpecs.length; si++) {
      const spec = newSpecs[si];
      if (!spec.values || spec.values.length === 0) continue;
      const newCombinations = [];
      for (const combo of combinations) {
        for (let vi = 0; vi < spec.values.length; vi++) {
          newCombinations.push([...combo, { spec_index: si, value_index: vi }]);
        }
      }
      combinations = newCombinations;
    }

    return combinations.map(spec_values => ({
      price:       '',
      vip_price:   '',
      stock:       0,
      image:       '',
      sku_code:    '',
      spec_values,
    }));
  };

  // 添加规格类型
  const addSpec = () => {
    const newSpecs = [...specs, { name: '', name_en: '', name_km: '', values: [] }];
    const newSkus  = generateSkuCombinations(newSpecs);
    onChange(newSpecs, newSkus);
  };

  // 删除规格类型
  const removeSpec = (si) => {
    const newSpecs = specs.filter((_, i) => i !== si);
    const newSkus  = generateSkuCombinations(newSpecs);
    onChange(newSpecs, newSkus);
  };

  // 更新规格类型名称（中/英/高棉）
  const updateSpec = (si, field, value) => {
    const newSpecs = specs.map((s, i) => i === si ? { ...s, [field]: value } : s);
    onChange(newSpecs, skus);
  };

  // 添加规格值
  const addSpecValue = (si) => {
    const newSpecs = specs.map((s, i) => {
      if (i !== si) return s;
      return { ...s, values: [...(s.values || []), { value: '', value_en: '', value_km: '' }] };
    });
    const newSkus = generateSkuCombinations(newSpecs);
    onChange(newSpecs, newSkus);
  };

  // 删除规格值
  const removeSpecValue = (si, vi) => {
    const newSpecs = specs.map((s, i) => {
      if (i !== si) return s;
      return { ...s, values: s.values.filter((_, j) => j !== vi) };
    });
    const newSkus = generateSkuCombinations(newSpecs);
    onChange(newSpecs, newSkus);
  };

  // 更新规格值（中/英/高棉）
  const updateSpecValue = (si, vi, field, value) => {
    const newSpecs = specs.map((s, i) => {
      if (i !== si) return s;
      const newValues = s.values.map((v, j) => j === vi ? { ...v, [field]: value } : v);
      return { ...s, values: newValues };
    });
    onChange(newSpecs, skus);
  };

  // 更新SKU字段
  const updateSku = (ki, field, value) => {
    const newSkus = skus.map((s, i) => i === ki ? { ...s, [field]: value } : s);
    onChange(specs, newSkus);
  };

  // 上传SKU图片（本地文件）
  const handleSkuImageUpload = async (ki, file) => {
    if (!file) return;
    setUploadingSkuIndex(ki);
    try {
      const res = await uploadApi.uploadProductImage(file);
      if (res?.data?.url) {
        updateSku(ki, 'image', res.data.url);
        setUrlErrorIndex(null);
      }
    } catch (err) {
      alert('图片上传失败: ' + err.message);
    } finally {
      setUploadingSkuIndex(null);
    }
  };

  // 打开/关闭 URL 输入框
  const toggleUrlInput = (ki) => {
    if (urlInputIndex === ki) {
      setUrlInputIndex(null);
      setUrlInputValue('');
    } else {
      setUrlInputIndex(ki);
      setUrlInputValue('');
      setUrlErrorIndex(null);
    }
  };

  // 确认URL链接（失焦或回车时自动调用，不再需要手动点确定按钮）
  const confirmUrlInput = (ki) => {
    const url = urlInputValue.trim();
    if (!url) {
      setUrlInputIndex(null);
      return;
    }
    updateSku(ki, 'image', url);
    setUrlInputIndex(null);
    setUrlInputValue('');
    setUrlErrorIndex(null);
  };

  // 获取SKU标签（如：红色/S）
  const getSkuLabel = (sku) => {
    if (!sku.spec_values || sku.spec_values.length === 0) return '-';
    return sku.spec_values.map(sv => {
      const spec  = specs[sv.spec_index];
      const value = spec?.values?.[sv.value_index];
      return value?.value || '-';
    }).join(' / ');
  };

  const handleZoomOpen = (sku) => {
    if (!sku.image) return;
    setZoomImage({ src: sku.image, label: getSkuLabel(sku) });
  };

  return (
    <div className="space-y-4">
      {/* 规格类型编辑 */}
      <div className="space-y-3">
        {specs.map((spec, si) => (
          <div key={si} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
            {/* 规格类型名称：中/英/高棉三语 */}
            <div className="flex items-start gap-2 mb-3">
              <span className="text-sm font-medium text-gray-700 w-16 flex-shrink-0 pt-2">规格{si + 1}</span>
              <div className="flex-1 grid grid-cols-3 gap-2">
                <input
                  type="text"
                  value={spec.name}
                  onChange={e => updateSpec(si, 'name', e.target.value)}
                  placeholder="规格名称（如：颜色）"
                  className="px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={spec.name_en}
                  onChange={e => updateSpec(si, 'name_en', e.target.value)}
                  placeholder="English"
                  className="px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={spec.name_km || ''}
                  onChange={e => updateSpec(si, 'name_km', e.target.value)}
                  placeholder="ភាសាខ្មែរ"
                  className="px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button type="button" onClick={() => removeSpec(si)}
                className="text-red-400 hover:text-red-600 flex-shrink-0 pt-2">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            {/* 规格值：每个值一张小卡片，中/英/高棉三语纵向排列 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-2">
              {(spec.values || []).map((val, vi) => (
                <div key={vi} className="bg-white border rounded-lg p-2 space-y-1.5 relative">
                  <button type="button" onClick={() => removeSpecValue(si, vi)}
                    className="absolute top-1.5 right-1.5 text-gray-300 hover:text-red-500">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                  <input
                    type="text"
                    value={val.value}
                    onChange={e => updateSpecValue(si, vi, 'value', e.target.value)}
                    placeholder="规格值（如：红色）"
                    className="w-full pr-6 text-sm border-none outline-none font-medium text-gray-800"
                  />
                  <input
                    type="text"
                    value={val.value_en || ''}
                    onChange={e => updateSpecValue(si, vi, 'value_en', e.target.value)}
                    placeholder="English"
                    className="w-full text-xs border-none outline-none text-gray-500"
                  />
                  <input
                    type="text"
                    value={val.value_km || ''}
                    onChange={e => updateSpecValue(si, vi, 'value_km', e.target.value)}
                    placeholder="ភាសាខ្មែរ"
                    className="w-full text-xs border-none outline-none text-gray-500"
                  />
                </div>
              ))}
              <button type="button" onClick={() => addSpecValue(si)}
                className="flex items-center justify-center gap-1 px-3 py-2 border border-dashed border-blue-400 text-blue-500 rounded-lg text-sm hover:bg-blue-50 min-h-[72px]">
                <Plus className="w-3 h-3" />添加规格值
              </button>
            </div>
          </div>
        ))}
        {specs.length < 2 && (
          <button type="button" onClick={addSpec}
            className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-400 text-gray-600 rounded-xl text-sm hover:bg-gray-50 w-full justify-center">
            <Plus className="w-4 h-4" />
            添加规格类型（最多2种）
          </button>
        )}
      </div>

      {/* SKU列表 */}
      {skus.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-700 mb-1">
            SKU 列表（共 {skus.length} 个组合）
          </p>
          {/* 价格留空说明单独一行，不再挤在表头里占空间 */}
          <p className="text-xs text-gray-400 mb-2">
            价格留空则使用主价 ${mainPrice || '0.00'}，VIP价留空则使用主VIP价
            {mainVipPrice ? ` $${mainVipPrice}` : '（主商品也没设VIP价的话，该规格就没有VIP价）'}
          </p>
          <div className="border rounded-xl overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 w-28">规格</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 w-24">价格</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 w-24">VIP价</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 w-20">库存</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 w-36">條碼</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 w-48">图片</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {skus.map((sku, ki) => (
                  <tr key={ki} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-800">
                      {getSkuLabel(sku)}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number" step="0.01" min="0"
                        value={sku.price}
                        onChange={e => updateSku(ki, 'price', e.target.value)}
                        placeholder={mainPrice || '同主价'}
                        className="w-full px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number" step="0.01" min="0"
                        value={sku.vip_price}
                        onChange={e => updateSku(ki, 'vip_price', e.target.value)}
                        placeholder={mainVipPrice || '无'}
                        className="w-full px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number" min="0" step="1"
                        value={sku.stock}
                        onChange={e => updateSku(ki, 'stock', e.target.value)}
                        className="w-full px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-3 py-2">
                      {/* 条码列加宽，避免长条码被压缩看不全 */}
                      <input
                        type="text"
                        value={sku.sku_code || ''}
                        onChange={e => updateSku(ki, 'sku_code', e.target.value)}
                        placeholder="可選"
                        className="w-full px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {sku.image ? (
                          <img
                            src={sku.image}
                            alt="sku"
                            className="w-8 h-8 object-cover rounded border cursor-zoom-in hover:ring-2 hover:ring-blue-400 transition-all flex-shrink-0"
                            onClick={() => handleZoomOpen(sku)}
                            title="点击查看大图"
                            onError={() => setUrlErrorIndex(ki)}
                          />
                        ) : (
                          <div className="w-8 h-8 bg-gray-100 rounded border flex items-center justify-center flex-shrink-0">
                            <ImageIcon className="w-4 h-4 text-gray-400" />
                          </div>
                        )}
                        <label className="cursor-pointer text-blue-500 hover:text-blue-700 text-xs flex-shrink-0">
                          <input type="file" accept="image/*" className="hidden"
                            onChange={e => handleSkuImageUpload(ki, e.target.files[0])}
                            disabled={uploadingSkuIndex !== null}
                          />
                          {uploadingSkuIndex === ki ? '上传中...' : '上传'}
                        </label>
                        <button type="button"
                          onClick={() => toggleUrlInput(ki)}
                          className="text-gray-400 hover:text-blue-600 flex-shrink-0"
                          title="输入图片链接">
                          <LinkIcon className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {urlInputIndex === ki && (
                        <div className="mt-1.5">
                          <input
                            type="text"
                            value={urlInputValue}
                            onChange={e => setUrlInputValue(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                e.target.blur();
                              }
                            }}
                            onBlur={() => confirmUrlInput(ki)}
                            placeholder="貼上圖片URL，失焦即自动保存"
                            className="w-full px-2 py-1 border rounded text-xs focus:ring-2 focus:ring-blue-500"
                            autoFocus
                          />
                        </div>
                      )}
                      {urlErrorIndex === ki && (
                        <p className="text-xs text-red-500 mt-1">圖片鏈接無效</p>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 图片放大预览遮罩层 */}
      {zoomImage && (
        <div
          className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4"
          onClick={() => setZoomImage(null)}
        >
          <button
            type="button"
            onClick={() => setZoomImage(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
            title="关闭"
          >
            <X className="w-8 h-8" />
          </button>
          {zoomImage.label && zoomImage.label !== '-' && (
            <div className="absolute top-4 left-4 text-white/90 text-sm font-medium bg-black/40 px-3 py-1.5 rounded-lg">
              {zoomImage.label}
            </div>
          )}
          <img
            src={zoomImage.src}
            alt={zoomImage.label}
            className="max-w-full max-h-full rounded-lg shadow-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default ProductSpecEditor;